import { useCallback, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Dialog } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useDropzone } from 'react-dropzone';
import { Storage } from 'aws-amplify';
import { useSnackbar } from 'notistack';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { PropTypes } from 'prop-types';
import { displayTagOfThumbAttach } from '@/component/ticketKnowledge/handler/ListTicketKnowledgeHandler';
import { getLinkDownloadFileFromS3 } from './FileUploaded';
import S3PathUtils from '@/common/utils/S3PathUtil';
import './ReplaceFileDialog.css';
import trI18n from '@/common/utils/trI18n';
import useTranslationSubscription from '@/hooks/useTranslation';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

/**
 * Get file extension from filename (lowercase, without dot)
 */
const getFileExtension = (fileName) => {
    if (!fileName || typeof fileName !== 'string') return '';
    const parts = fileName.split('.');
    if (parts.length < 2) return '';
    return parts.pop().toLowerCase();
};

/**
 * ReplaceFileDialog - Popup to select a replacement file
 *
 * Thumbnail logic matches detail ticket exactly:
 * - Image files (Utils.isImageFile): show actual thumbnail from CDN (same as renderFileImageUpload)
 * - Non-image files: show others_file.png + badge (same as renderFileUploaded)
 */
function ReplaceFileDialog({
    isOpen,
    onClose,
    originalFileName,
    originalFileUrl,
    onReplace,
    detailTicket,
    limitFileSize,
}) {
    useTranslationSubscription();
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [tmpS3Path, setTmpS3Path] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const originalExtension = getFileExtension(originalFileName);
    const orgId = detailTicket?.affiliationOrganization;
    const isOriginalImage = Utils.isImageFile(originalFileName);

    // Badge (same logic as detail ticket)
    const originalFileBadge = useMemo(() => {
        return displayTagOfThumbAttach(detailTicket || {}, originalFileName);
    }, [detailTicket, originalFileName]);

    // Thumbnail URL for original file (same as renderFileImageUpload in EditFileUploaded)
    const originalThumbnailUrl = useMemo(() => {
        if (!isOriginalImage || !originalFileUrl) return '';
        const thumbnailInfo = detailTicket?.attachFiles?.find(
            att => att?.fileInfo?.fileUrl === originalFileUrl
        )?.thumbnailInfo;
        return getLinkDownloadFileFromS3(
            {
                fileUrl: originalFileUrl,
                fileName: originalFileName,
                type: Constant.S3_FILE_TYPE.TICKET_FILE,
                thumbnailInfo: thumbnailInfo ? JSON.stringify(thumbnailInfo) : null
            },
            detailTicket,
            null,
            Constant.ATTACH_FILE.VIEW_TICKET_DETAIL
        ) || '';
    }, [isOriginalImage, originalFileUrl, originalFileName, detailTicket]);

    // Preview URL for selected file (local blob for images)
    const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState('');

    useEffect(() => {
        if (!selectedFile || !Utils.isImageFile(selectedFile.name)) {
            setSelectedFilePreviewUrl('');
            return;
        }
        const url = URL.createObjectURL(selectedFile);
        setSelectedFilePreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [selectedFile]);

    const selectedFileBadge = useMemo(() => {
        if (!selectedFile) return null;
        return displayTagOfThumbAttach(detailTicket || {}, selectedFile.name);
    }, [detailTicket, selectedFile]);

    const validateFileExtension = useCallback((file) => {
        const newExtension = getFileExtension(file.name);
        return newExtension === originalExtension;
    }, [originalExtension]);

    const uploadToS3Tmp = useCallback(async (file) => {
        const tmpKey = S3PathUtils.getTmpReplaceFilePath(orgId, detailTicket?.id, file.name);
        if (!tmpKey) return '';
        await Storage.put(tmpKey, file, {
            contentType: file.type,
            level: 'public',
            contentDisposition: `inline; filename=${encodeURIComponent(file.name)}`
        });
        return tmpKey;
    }, [orgId, detailTicket?.id]);

    const onDrop = useCallback(async (acceptedFiles) => {
        if (Utils.isEmptyArray(acceptedFiles)) return;

        const file = acceptedFiles[0];
        if (!validateFileExtension(file)) {
            setErrorMessage(trI18n('MSG-REPLACE-FILE-EXTENSION-MISMATCH'));
            setSelectedFile(null);
            setTmpS3Path('');
            return;
        }

        setErrorMessage('');
        setSelectedFile(file);
        setIsUploading(true);

        try {
            const uploadedKey = await uploadToS3Tmp(file);
            setTmpS3Path(uploadedKey);
        } catch (error) {
            setErrorMessage(trI18n('MSG-REPLACE-FILE-UPLOAD-FAILED'));
            setSelectedFile(null);
            setTmpS3Path('');
        } finally {
            setIsUploading(false);
        }
    }, [validateFileExtension, uploadToS3Tmp, limitFileSize]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        disabled: isUploading
    });

    const handleRemoveSelected = () => {
        setSelectedFile(null);
        setTmpS3Path('');
        setErrorMessage('');
    };

    const handleReplace = () => {
        if (!selectedFile || !tmpS3Path) return;

        // Validate file size for business app (limitFileSize in MB)
        if (limitFileSize && selectedFile.size > limitFileSize * 1024 * 1024) {
            enqueueSnackbar(
                buildSnackbarMessage('ERROR_MESSAGE_OVER_FILE_SIZE', { '{fileSize}': `${limitFileSize}MB` }),
                { variant: 'error', autoHideDuration: 2000 }
            );
            handleClose();
            return;
        }

        onReplace(selectedFile, tmpS3Path);
        handleClose();
    };

    const handleClose = () => {
        if (isUploading) return;
        setSelectedFile(null);
        setTmpS3Path('');
        setErrorMessage('');
        setIsUploading(false);
        onClose();
    };

    /**
     * Render file thumbnail - matches detail ticket:
     * - Image: actual thumbnail (70x50, object-fit cover) — same as renderFileImageUpload
     * - Non-image: others_file.png (70x50) + badge — same as renderFileUploaded
     */
    const renderFileThumbnail = (fileName, imgUrl, badge) => {
        const isImage = Utils.isImageFile(fileName);
        const imgSrc = isImage && imgUrl ? imgUrl : '/assets/image/others_file.png';

        return (
            <div className="replace-file-thumbnail">
                <img
                    src={imgSrc}
                    alt=""
                    className="replace-file-thumbnail-img"
                />
                {badge?.name && (
                    <span
                        className="replace-file-thumbnail-badge"
                        style={{ backgroundColor: badge.colorCode }}
                    >
                        {badge.name}
                    </span>
                )}
            </div>
        );
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            className="replace-file-dialog"
            disableBackdropClick
        >
            {isUploading && (
                <div className="replace-file-loading-overlay">
                    <CircularProgress size={40} />
                </div>
            )}
            <div className="replace-file-dialog-header">
                <h3>{trI18n('LABEL-REPLACE-FILE-TITLE')}</h3>
            </div>

            <div className="replace-file-dialog-body">
                {/* Current file - same display as detail ticket */}
                <div className="replace-file-current-file">
                    <div className="replace-file-current-file-label">
                        {trI18n('LABEL-REPLACE-FILE-CURRENT')}
                    </div>
                    <div className="replace-file-current-file-info">
                        {renderFileThumbnail(originalFileName, originalThumbnailUrl, originalFileBadge)}
                        <div className="replace-file-current-file-name">
                            {originalFileName}
                        </div>
                    </div>
                </div>

                {/* Info banner */}
                <div className="replace-file-info-banner">
                    <div className="replace-file-info-banner-icon">i</div>
                    <div className="replace-file-info-banner-text">
                        {trI18n('MSG-REPLACE-FILE-INFO-BANNER')}
                    </div>
                </div>

                {/* Dropzone or selected file */}
                {!selectedFile ? (
                    <div
                        {...getRootProps()}
                        className={`replace-file-dropzone ${
                            isDragActive ? 'replace-file-dropzone-active' : ''
                        }`}
                    >
                        <input {...getInputProps()} />
                        <div className="replace-file-dropzone-icon">
                            <img src="/assets/image/replace-file-upload.svg" alt="" />
                        </div>
                        <div className="replace-file-dropzone-main-text">
                            {trI18n('MSG-REPLACE-FILE-SELECT-NEW')}
                        </div>
                        <div className="replace-file-dropzone-sub-text">
                            {trI18n('MSG-REPLACE-FILE-OR-DRAG')}
                        </div>
                    </div>
                ) : (
                    <div className="replace-file-selected">
                        <div className="replace-file-selected-info">
                            {renderFileThumbnail(selectedFile.name, selectedFilePreviewUrl, selectedFileBadge)}
                            <span className="replace-file-selected-name">
                                {selectedFile.name}
                            </span>
                        </div>
                        {!isUploading && (
                            <span
                                className="replace-file-selected-remove"
                                onClick={handleRemoveSelected}
                            >
                                <img src="/assets/image/delete-icon.svg" alt="" />
                            </span>
                        )}
                    </div>
                )}

                {/* Error message */}
                {errorMessage && (
                    <div className="replace-file-error">
                        <InfoOutlinedIcon className="replace-file-error-icon" />
                        <span className="replace-file-error-text">
                            {errorMessage}
                        </span>
                    </div>
                )}
            </div>

            <div className="replace-file-dialog-footer">
                <button
                    className="replace-file-btn-cancel"
                    onClick={handleClose}
                    disabled={isUploading}
                >
                    {trI18n('CM-BUTTON-CANCEL')}
                </button>
                <button
                    className="replace-file-btn-submit"
                    onClick={handleReplace}
                    disabled={!selectedFile || !tmpS3Path || isUploading}
                >
                    {trI18n('LABEL-REPLACE-FILE-SUBMIT')}
                </button>
            </div>
        </Dialog>
    );
}

ReplaceFileDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    originalFileName: PropTypes.string.isRequired,
    originalFileUrl: PropTypes.string,
    onReplace: PropTypes.func.isRequired,
    detailTicket: PropTypes.object,
    limitFileSize: PropTypes.number,
};

export default ReplaceFileDialog;
